import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { AuthLayout } from "@/pages/AuthLayout";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
      toast.success("Reset link requested");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link to set a new password."
      footer={<Link to="/login" className="text-brand hover:underline" data-testid="forgot-back-to-login">Back to sign in</Link>}>
      {sent ? (
        <div className="flex gap-3" data-testid="forgot-password-success">
          <MailCheck className="text-success shrink-0" size={20} />
          <p className="text-sm text-ink-secondary">{sent}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Choose a new password" subtitle="Use at least 8 characters.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="rp-pass">New password</Label>
          <Input id="rp-pass" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-btn">Update password</Button>
      </form>
    </AuthLayout>
  );
}
